import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and selected org to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add selected organization header for Super Admin context
  try {
    const selectedOrg = sessionStorage.getItem('selectedOrg');
    if (selectedOrg) {
      const org = JSON.parse(selectedOrg);
      if (org && org.id) {
        config.headers['X-Selected-Organization'] = org.id;
      }
    }
  } catch {
    // Ignore parsing errors
  }
  
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// User Invites
export const inviteUser = (data) => api.post('/users/invite', data);
export const getUserInvites = (params) => api.get('/users/invites', { params });
export const getInviteByToken = (token) => api.get(`/users/invite/${token}`);
export const acceptInvite = (token, password) => api.post(`/users/invite/${token}/accept?password=${encodeURIComponent(password)}`);
export const cancelInvite = (inviteId) => api.delete(`/users/invite/${inviteId}`);
export const resendInvite = (inviteId) => api.post(`/users/invite/${inviteId}/resend`);

// User Management (CRUD)
export const getUsers = () => api.get('/users');
export const getUser = (userId) => api.get(`/users/${userId}`);
export const updateUser = (userId, data) => api.put(`/users/${userId}`, data);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// Portfolio/Network Intelligence (for org admins with child orgs)
export const getPortfolioIntelligence = (params) => api.get('/portfolio/intelligence', { params });
export const getPortfolioOrganizationsDrilldown = (params) => api.get('/portfolio/organizations-drilldown', { params });

// Organizations
export const getOrganizations = (params) => api.get('/organizations', { params });
export const getOrganization = (id) => api.get(`/organizations/${id}`);
export const createOrganization = (data) => api.post('/organizations', data);
export const updateOrganization = (id, data) => api.put(`/organizations/${id}`, data);
export const deleteOrganization = (id) => api.delete(`/organizations/${id}`);
export const getOrganizationHierarchy = (id) => api.get(`/organizations/${id}/hierarchy`);
export const getOrganizationIntelligence = (id) => api.get(`/organizations/${id}/intelligence`);
export const searchOrganizationWeb = (id) => api.post(`/organizations/${id}/web-search`);
export const getLatestWebSearch = (id) => api.get(`/organizations/${id}/web-search/latest`);
export const get990TaxData = (id) => api.get(`/organizations/${id}/990-data`);
export const getLatest990Data = (id) => api.get(`/organizations/${id}/990-data/latest`);
export const uploadOrgLogo = (orgId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/organizations/${orgId}/upload-logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getOrgBySlug = (slug) => api.get(`/organizations/by-slug/${slug}`);
export const updateNonprofitInfo = (id, data) => api.put(`/organizations/${id}/nonprofit-info`, null, { params: data });
export const autoPopulateOrg = (id) => api.post(`/organizations/${id}/auto-populate`);
export const applyAutoPopulate = (id, data) => api.post(`/organizations/${id}/auto-populate/apply`, data);

// Assessments
export const getAssessmentQuestions = (orgType = 'sports') => api.get('/assessments/questions', { params: { org_type: orgType } });
export const getAssessments = (params) => api.get('/assessments', { params });
export const getAssessment = (id) => api.get(`/assessments/${id}`);
export const createAssessment = (data) => api.post('/assessments', data);
export const checkAssessmentEligibility = (orgId) => api.get(`/assessments/eligibility/${orgId}`);

// Assessment-Led Onboarding (ALO) - Public endpoints
export const searchNationalOrganizations = (query, orgType = 'sports') => 
  api.get('/alo/organizations/search', { params: { q: query, org_type: orgType } });
export const getExternalAssessmentQuestions = (orgType = 'sports') => 
  api.get('/alo/assessment/questions', { params: { org_type: orgType } });
export const submitExternalAssessment = (data) => api.post('/alo/assessment/submit', data);
export const getOnboardingDetails = (token) => api.get(`/alo/onboarding/${token}`);
export const completeOnboarding = (token, password) => 
  api.post(`/alo/onboarding/${token}/complete?password=${encodeURIComponent(password)}`);

// ALO Admin endpoints
export const getALOPendingReviews = () => api.get('/alo/admin/pending-reviews');
export const resolveHierarchy = (orgId, action, params) => 
  api.post(`/alo/admin/resolve-hierarchy/${orgId}`, null, { params: { action, ...params } });
export const getHierarchyLogs = (params) => api.get('/alo/admin/hierarchy-logs', { params });
export const overrideAssessment = (assessmentId, data) => 
  api.post(`/alo/admin/override-assessment/${assessmentId}`, null, { params: data });

// Incidents
export const getIncidents = (params) => api.get('/incidents', { params });
export const getIncident = (id) => api.get(`/incidents/${id}`);
export const createIncident = (data) => api.post('/incidents', data);
export const updateIncident = (id, data) => api.put(`/incidents/${id}`, data);
export const analyzeIncident = (id) => api.post(`/incidents/${id}/analyze`);
export const updateIncidentChecklist = (incidentId, itemId, completed) => 
  api.put(`/incidents/${incidentId}/checklist/${itemId}?completed=${completed}`);
export const uploadIncidents = (formData) => api.post('/incidents/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const getIncidentCategories = () => api.get('/incidents/categories');

// Public Incident Reporting Links
export const createPublicIncidentLink = (data) => api.post('/incidents/public-links', data);
export const getPublicIncidentLinks = (organizationId) => api.get('/incidents/public-links', { params: organizationId ? { organization_id: organizationId } : {} });
export const revokePublicIncidentLink = (linkId) => api.delete(`/incidents/public-links/${linkId}`);
export const updatePublicIncidentLink = (linkId, data) => api.put(`/incidents/public-links/${linkId}`, data);
export const getPublicLinkInfo = (token) => axios.get(`${API_URL}/incidents/public/${token}`);
export const submitPublicIncident = (token, data) => axios.post(`${API_URL}/incidents/public/${token}`, data);

// Incident Workflow Management
export const getIncidentWorkflowStats = (orgId) => api.get('/incidents/workflow/stats', { params: { organization_id: orgId } });
export const getIncidentWorkflowBoard = (orgId) => api.get('/incidents/workflow/board', { params: { organization_id: orgId } });
export const addIncidentNote = (incidentId, data) => api.post(`/incidents/${incidentId}/notes`, data);
export const getIncidentNotes = (incidentId) => api.get(`/incidents/${incidentId}/notes`);
export const assignIncident = (incidentId, adminId) => api.post(`/incidents/${incidentId}/assign?admin_id=${adminId}`);
export const escalateIncident = (incidentId, reason) => api.post(`/incidents/${incidentId}/escalate?reason=${encodeURIComponent(reason)}`);
export const bulkUpdateIncidents = (data) => api.post('/incidents/bulk-update', data);
export const getAdminList = (orgId) => api.get('/admins/list', { params: { organization_id: orgId } });
export const getIncidentConfig = (orgId) => api.get(`/incidents/config/${orgId}`);
export const updateIncidentConfig = (orgId, data) => api.put(`/incidents/config/${orgId}`, data);

// Events
export const getEvents = (params) => api.get('/events', { params });
export const createEvent = (data) => api.post('/events', data);
export const analyzeEvent = (id) => api.post(`/events/${id}/analyze`);

// Claims
export const getClaims = (params) => api.get('/claims', { params });
export const createClaim = (data) => api.post('/claims', data);
export const uploadClaims = (formData) => api.post('/claims/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const getClaimsAnalytics = (params) => api.get('/claims/analytics', { params });

// Alerts
export const getAlerts = (params) => api.get('/alerts', { params });
export const markAlertRead = (id) => api.put(`/alerts/${id}/read`);
export const simulateWebMonitoring = (organizationId) => 
  api.post('/alerts/simulate-web-monitoring', null, { params: { organization_id: organizationId } });

// Notifications
export const getNotificationConfig = () => api.get('/notifications/config');
export const getNotificationPreferences = () => api.get('/notifications/preferences');
export const updateNotificationPreferences = (data) => api.put('/notifications/preferences', data);
export const sendNotification = (data) => api.post('/notifications/send', data);
export const sendTestEmail = () => api.post('/notifications/test-email');
export const triggerRenewalCheck = () => api.post('/notifications/trigger-renewal-check');
export const sendWeeklyDigest = (nationalOrgId) => api.post(`/notifications/send-weekly-digest/${nationalOrgId}`);
export const getNotificationEvents = (params) => api.get('/notifications/events', { params });
export const getNotificationDeliveries = (params) => api.get('/notifications/deliveries', { params });

// User Org Roles
export const createUserOrgRole = (data) => api.post('/user-org-roles', data);
export const getUserOrgRoles = (userId) => api.get(`/user-org-roles/${userId}`);
export const deleteUserOrgRole = (roleId) => api.delete(`/user-org-roles/${roleId}`);

// Staff
// Staff & Board API functions removed - focusing on user invites only
// export const getStaff = (params) => api.get('/staff', { params });
// export const getStaffMember = (id) => api.get(`/staff/${id}`);
// export const createStaff = (data) => api.post('/staff', data);
// export const updateStaff = (id, data) => api.put(`/staff/${id}`, data);
// export const deleteStaff = (id) => api.delete(`/staff/${id}`);
// export const runBackgroundCheck = (id) => api.post(`/staff/${id}/background-check`);

// Policies
export const getPolicies = (params) => api.get('/policies', { params });
export const getPolicy = (id) => api.get(`/policies/${id}`);
export const createPolicy = (data) => api.post('/policies', data);

// Trainings
export const getTrainings = (params) => api.get('/trainings', { params });
export const createTraining = (data) => api.post('/trainings', data);

// ==================== LMS & POLICY GOVERNANCE ====================

// Platform Policies (Super Admin)
export const getPlatformPolicies = (params) => api.get('/platform/policies', { params });
export const getPlatformPolicy = (id) => api.get(`/platform/policies/${id}`);
export const createPlatformPolicy = (data) => api.post('/platform/policies', data);
export const updatePlatformPolicy = (id, data) => api.put(`/platform/policies/${id}`, data);
export const deletePlatformPolicy = (id) => api.delete(`/platform/policies/${id}`);

// Implementation Guides
export const createImplementationGuide = (data) => api.post('/platform/implementation-guides', data);
export const getImplementationGuide = (id) => api.get(`/platform/implementation-guides/${id}`);
export const getPolicyImplementationGuide = (policyId) => api.get(`/platform/policies/${policyId}/implementation-guide`);

// Policy Assignments
export const getPolicyAssignments = (params) => api.get('/platform/policy-assignments', { params });
export const assignPolicyToOrg = (data) => api.post('/platform/policy-assignments', data);
export const acknowledgePolicy = (assignmentId) => api.put(`/platform/policy-assignments/${assignmentId}/acknowledge`);

// Training Modules (LMS)
export const getTrainingModules = (params) => api.get('/lms/modules', { params });
export const getTrainingModule = (id) => api.get(`/lms/modules/${id}`);
export const createTrainingModule = (data) => api.post('/lms/modules', data);
export const updateTrainingModule = (id, data) => api.put(`/lms/modules/${id}`, data);
export const deleteTrainingModule = (id) => api.delete(`/lms/modules/${id}`);

// Video Upload for Training
export const uploadTrainingVideo = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/lms/upload-video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

// Video Progress Tracking
export const trackVideoProgress = (trainingModuleId, organizationId, progressPercentage, timeSpentSeconds) => 
  api.post('/lms/progress/video-view', null, { 
    params: { 
      training_module_id: trainingModuleId, 
      organization_id: organizationId,
      progress_percentage: progressPercentage,
      time_spent_seconds: timeSpentSeconds
    } 
  });

export const getMyVideoProgress = (trainingModuleId, organizationId) => 
  api.get(`/lms/progress/my-video/${trainingModuleId}`, { params: { organization_id: organizationId } });

// Training Assignments
export const getTrainingAssignments = (params) => api.get('/lms/assignments', { params });
export const createTrainingAssignment = (data) => api.post('/lms/assignments', data);

// Training Progress
export const startTraining = (moduleId, organizationId) => api.post(`/lms/progress/${moduleId}/start`, null, { params: { organization_id: organizationId } });
export const updateTrainingProgress = (progressId, data) => api.put(`/lms/progress/${progressId}`, data);
export const getTrainingProgress = (params) => api.get('/lms/progress', { params });
export const getMyTrainingProgress = () => api.get('/lms/my-progress');

// Learning Recommendations
export const getLearningRecommendations = (organizationId) => api.get(`/lms/recommendations/${organizationId}`);

// LMS Stats
export const getLMSStats = (params) => api.get('/lms/stats', { params });

// Dashboard
export const getDashboardStats = (params) => api.get('/dashboard/stats', { params });

// ==================== PORTFOLIO ANALYTICS ====================

// Portfolio Summary (Executive View)
export const getPortfolioSummary = (params) => api.get('/portfolio/summary', { params });

// Portfolio Assessments Analytics
export const getPortfolioAssessments = (params) => api.get('/portfolio/assessments', { params });

// Portfolio Assessments List (individual assessments for clicking)
export const getPortfolioAssessmentsList = (params) => api.get('/portfolio/assessments/list', { params });

// Portfolio Incidents Analytics
export const getPortfolioIncidents = (params) => api.get('/portfolio/incidents', { params });

// Portfolio Claims Analytics
export const getPortfolioClaims = (params) => api.get('/portfolio/claims', { params });

// Portfolio Events Analytics
export const getPortfolioEvents = (params) => api.get('/portfolio/events', { params });

// Cross-Domain Pattern Analysis
export const getPortfolioCrossDomain = (params) => api.get('/portfolio/cross-domain', { params });

// Portfolio Risk Signals
export const getPortfolioRiskSignals = (params) => api.get('/portfolio/risk-signals', { params });

// Portfolio Trends
export const getPortfolioTrends = () => api.get('/portfolio/trends');

// Portfolio LMS Analytics
export const getPortfolioLMS = (params) => api.get('/portfolio/lms', { params });

// Portfolio Professional Sports Summary
export const getPortfolioProfessionalSportsSummary = () => api.get('/portfolio/professional-sports-summary');
export const getPortfolioProfessionalSportsCriticalFlags = () => api.get('/portfolio/professional-sports-critical-flags');

// Admin
export const getAllOrganizations = () => api.get('/admin/all-organizations');
export const getAllUsers = () => api.get('/admin/users');
export const updateUserRole = (userId, role) => api.put(`/admin/users/${userId}/role`, null, { params: { role } });

// ==================== PROFESSIONAL SPORTS ASSESSMENT ====================

// Get assessment questions organized by pillar/section
export const getProfessionalSportsQuestions = (pillar, section) => 
  api.get('/professional-sports-assessment/questions', { params: { pillar, section } });

// Get assessment configuration (pillars, risk bands, critical flags)
export const getProfessionalSportsConfig = () => 
  api.get('/professional-sports-assessment/config');

// Submit a professional sports assessment
export const submitProfessionalSportsAssessment = (organizationId, answers) => 
  api.post(`/professional-sports-assessment/submit?organization_id=${organizationId}`, answers);

// Get assessment results by ID
export const getProfessionalSportsResults = (assessmentId) => 
  api.get(`/professional-sports-assessment/results/${assessmentId}`);

// Get dashboard data for an organization
export const getProfessionalSportsDashboard = (organizationId) => 
  api.get(`/professional-sports-assessment/dashboard/${organizationId}`);

// ==================== POLICIES V2 API ====================

// Policy Templates (Players Health Library)
export const getPolicyTemplates = (params) => api.get('/policies/v2/templates', { params });
export const getPolicyTemplate = (id) => api.get(`/policies/v2/templates/${id}`);
export const createPolicyTemplate = (data) => api.post('/policies/v2/templates', data);
export const updatePolicyTemplate = (id, data) => api.put(`/policies/v2/templates/${id}`, data);
export const deletePolicyTemplate = (id) => api.delete(`/policies/v2/templates/${id}`);

// Organization Policies
export const getOrganizationPoliciesV2 = (params) => api.get('/policies/v2/organization', { params });
export const getOrganizationPolicy = (id) => api.get(`/policies/v2/organization/${id}`);
export const createOrganizationPolicyV2 = (data) => api.post('/policies/v2/organization', data);
export const uploadOrganizationPolicyFile = (formData) => api.post('/policies/v2/organization/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadTemplateTrainingVideo = (templateId, formData, onUploadProgress) =>
  api.post(`/policies/v2/templates/${templateId}/upload-video`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  });
export const deleteTemplateTrainingVideo = (templateId, videoId) =>
  api.delete(`/policies/v2/templates/${templateId}/videos/${videoId}`);
export const reorderTemplateTrainingVideos = (templateId, videoIds) =>
  api.post(`/policies/v2/templates/${templateId}/reorder-videos`, { video_ids: videoIds });
export const uploadOrgPolicyTrainingVideo = (policyId, formData, onUploadProgress) =>
  api.post(`/policies/v2/organization/${policyId}/upload-video`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  });
export const deleteOrgPolicyTrainingVideo = (policyId, videoId) =>
  api.delete(`/policies/v2/organization/${policyId}/videos/${videoId}`);
export const reorderOrgPolicyTrainingVideos = (policyId, videoIds) =>
  api.post(`/policies/v2/organization/${policyId}/reorder-videos`, { video_ids: videoIds });
export const updateOrganizationPolicy = (id, data) => api.put(`/policies/v2/organization/${id}`, data);
export const deleteOrganizationPolicy = (id) => api.delete(`/policies/v2/organization/${id}`);
export const getPolicyChangeHistory = (id) => api.get(`/policies/v2/organization/${id}/history`);
export const getRequiredPolicies = () => api.get('/policies/v2/organization/required');
export const getMyOrganizationPolicies = () => api.get('/policies/v2/organization/my-policies');
export const getAvailableTemplates = () => api.get('/policies/v2/organization/library');
export const downloadPolicyPdf = (id) => api.get(`/policies/v2/organization/${id}/pdf`, { responseType: 'blob' });

// Policy Adoption
export const adoptPolicyTemplate = (data) => api.post('/policies/v2/adopt', data);
export const markPolicyEquivalent = (data) => api.post('/policies/v2/mark-equivalent', data);
export const approvePolicyEquivalency = (id) => api.post(`/policies/v2/equivalency/${id}/approve`);

// Policy Compliance
export const getPolicyComplianceSummary = () => api.get('/policies/v2/compliance/summary');
export const getAllOrgsCompliance = () => api.get('/policies/v2/compliance/all-orgs');

// Risk Integration
export const triggerPoliciesFromAssessment = (assessmentId) => api.post(`/policies/v2/trigger-from-assessment/${assessmentId}`);
export const syncPoliciesFromRecommendations = (organizationId) => api.post(`/policies/v2/sync-from-recommendations/${organizationId}`);
export const getRiskPolicyMappings = () => api.get('/policies/v2/risk-mappings');

// Admin Functions
export const seedPolicyTemplates = () => api.post('/policies/v2/seed-templates');
export const migrateLegacyPolicies = () => api.post('/policies/v2/migrate-legacy');

// AI Policy Validation
export const validatePolicyAgainstTemplate = (policyId, templateId) =>
  api.post(`/policies/v2/organization/${policyId}/validate-against/${templateId}`);

// Broker Reassignment (Super Admin only)
export const reassignOrgBroker = (orgId, brokerId, reason) =>
  api.put(`/organizations/${orgId}/broker`, { broker_id: brokerId || null, reason: reason || '' });
export const getOrgBrokerHistory = (orgId) => api.get(`/organizations/${orgId}/broker-history`);

// Loss Run Analyzer
export const uploadLossRun = (formData) =>
  api.post('/loss-runs/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const listLossRuns = () => api.get('/loss-runs');
export const listLossRunsByOrg = (orgId) => api.get(`/loss-runs/by-org/${orgId}`);
export const getLossRun = (id) => api.get(`/loss-runs/${id}`);
export const analyzeLossRun = (id) => api.post(`/loss-runs/${id}/analyze`);
export const deleteLossRun = (id) => api.delete(`/loss-runs/${id}`);

// ==================== BROKER INTAKE SYSTEM API ====================

// Broker Management (Super Admin only)
export const createBroker = (data) => api.post('/broker/brokers', data);
export const getBrokers = (includeInactive = false) => api.get('/broker/brokers', { params: { include_inactive: includeInactive } });
export const getBroker = (brokerId) => api.get(`/broker/brokers/${brokerId}`);
export const updateBroker = (brokerId, data) => api.put(`/broker/brokers/${brokerId}`, data);
export const deleteBroker = (brokerId) => api.delete(`/broker/brokers/${brokerId}`);

// Profile/Account Management
export const updateProfile = (data) => api.put('/auth/me', data);

// Intake Links (Broker/Super Admin)
export const createIntakeLink = (data) => api.post('/broker/intake-links', data);
export const getIntakeLinks = (status) => api.get('/broker/intake-links', { params: status ? { status } : {} });
export const getIntakeLink = (linkId) => api.get(`/broker/intake-links/${linkId}`);
export const updateIntakeLink = (linkId, data) => api.put(`/broker/intake-links/${linkId}`, data);
export const revokeIntakeLink = (linkId) => api.delete(`/broker/intake-links/${linkId}`);
export const resendIntakeInvite = (linkId) => api.post(`/broker/intake-links/${linkId}/resend-email`);

// Public Intake Endpoints (No auth required)
export const getIntakeByToken = (token) => api.get(`/broker/public/intake/${token}`);
export const startIntake = (token, orgData) => api.post(`/broker/public/intake/${token}/start`, orgData);
export const submitProspectAssessment = (token, assessmentData) => api.post(`/broker/public/intake/${token}/assessment`, assessmentData);
export const uploadProspectDocument = (token, file, category, description, documentDate) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  if (description) formData.append('description', description);
  if (documentDate) formData.append('document_date', documentDate);
  return axios.post(`${API_URL}/broker/public/intake/${token}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const brokerUploadDocument = (orgId, file, category, description) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  if (description) formData.append('description', description);
  return api.post(`/broker/prospects/${orgId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const completeIntake = (token) => api.post(`/broker/public/intake/${token}/complete`);

// Broker Dashboard
export const getBrokerDashboardSummary = () => api.get('/broker/dashboard/summary');
export const getBrokerPipeline = (stage) => api.get('/broker/dashboard/pipeline', { params: stage ? { stage } : {} });

// Prospect Management
export const getProspectDetails = (orgId) => api.get(`/broker/prospects/${orgId}`);
export const getProspectDocuments = (orgId) => api.get(`/broker/prospects/${orgId}/documents`);
export const downloadProspectDocument = (orgId, docId) => 
  api.get(`/broker/prospects/${orgId}/documents/${docId}/download`, { responseType: 'blob' });
export const updateProspectStatus = (orgId, newStatus, notes) => 
  api.put(`/broker/prospects/${orgId}/status`, null, { params: { new_status: newStatus, notes } });
export const activateCustomer = (orgId, notes) => 
  api.post(`/broker/prospects/${orgId}/activate`, { organization_id: orgId, activation_notes: notes });

// Customer Onboarding
export const getCustomerOnboardingDetails = (token) => api.get(`/broker/customer-onboarding/${token}`);
export const completeCustomerOnboarding = (token, password) => 
  api.post(`/broker/customer-onboarding/${token}/complete`, { password });

// Utility
export const getLifecycleStatuses = () => api.get('/broker/lifecycle-statuses');
export const getDocumentCategories = () => api.get('/broker/document-categories');

// Content Hub — Partner Marketplace
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };
const toForm = (obj) => {
  const fd = new FormData();
  Object.entries(obj).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
  return fd;
};

// Safely extract a human-readable error message (FastAPI 422 detail can be an array of objects)
export const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.detail || '').filter(Boolean).join('; ') || fallback;
  if (d && typeof d === 'object') return d.msg || fallback;
  return fallback;
};
export const getPartners = () => api.get('/partners');
export const createPartner = (data) => api.post('/partners', toForm(data), multipart);
export const updatePartner = (id, data) => api.put(`/partners/${id}`, toForm(data), multipart);
export const deletePartner = (id) => api.delete(`/partners/${id}`);
export const uploadPartnerLogo = (id, file) => api.post(`/partners/${id}/logo`, toForm({ file }), multipart);
export const uploadPartnerOnesheet = (id, file, title) =>
  api.post(`/partners/${id}/onesheets`, toForm({ file, title }), multipart);
export const deletePartnerOnesheet = (id, sheetId) => api.delete(`/partners/${id}/onesheets/${sheetId}`);

// Content Hub — Guidance Library
export const getGuidance = () => api.get('/guidance');
export const uploadGuidance = (data) => api.post('/guidance', toForm(data), multipart);
export const updateGuidance = (id, data) => api.put(`/guidance/${id}`, toForm(data), multipart);
export const deleteGuidance = (id) => api.delete(`/guidance/${id}`);

// Content Hub — Explainer Videos
export const getExplainerVideos = () => api.get('/explainer-videos');
export const getExplainerVideo = (section) => api.get(`/explainer-videos/${section}`);
export const setExplainerVideoLink = (section, video_url, title) =>
  api.put(`/explainer-videos/${section}`, toForm({ video_url, title }), multipart);
export const uploadExplainerVideo = (section, file, title) =>
  api.post(`/explainer-videos/${section}/upload`, toForm({ file, title }), multipart);
export const deleteExplainerVideo = (section) => api.delete(`/explainer-videos/${section}`);

export default api;
